import type * as THREE from "three";
import { isNotNil } from "es-toolkit";
import { ALL_MUSCLE_IDS, type MuscleId } from "@/entities/muscle/model/muscles";

export type MuscleClassification =
  | { kind: "muscle"; muscleId: MuscleId }
  | { kind: "generic" }
  | { kind: "hidden" };

const MUSCLE_NAME_PATTERNS = {
  chest: [/pectoralis/i],
  back: [/latissimus[_\s]dorsi/i, /rhomboid/i, /\bteres\b/i, /infraspinatus/i, /supraspinatus/i],
  traps: [/trapezius/i, /levator[_\s]scapulae/i],
  shoulders: [/deltoid/i],
  biceps: [/biceps[_\s]brachii/i, /brachialis(?![a-z])/i, /coracobrachialis/i],
  triceps: [/triceps[_\s]brachii/i, /\banconeus\b/i],
  forearms: [
    /brachioradialis/i,
    /flexor[_\s]carpi/i,
    /extensor[_\s]carpi/i,
    /flexor[_\s]digitorum/i,
    /extensor[_\s]digitorum/i,
    /pronator/i,
    /supinator/i,
  ],
  abs: [/rectus[_\s]abdominis/i],
  obliques: [
    /abdominal[_\s]oblique/i,
    /external[_\s]oblique/i,
    /internal[_\s]oblique/i,
    /transversus[_\s]abdominis/i,
  ],
  glutes: [/gluteus[_\s](maximus|medius|minimus)/i],
  quads: [/rectus[_\s]femoris/i, /vastus[_\s](lateralis|medialis|intermedius)/i],
  hamstrings: [/biceps[_\s]femoris/i, /semitendinosus/i, /semimembranosus/i],
  calves: [/gastrocnemius/i, /\bsoleus\b/i, /tibialis[_\s]posterior/i],
  adductors: [
    /adductor[_\s](magnus|longus|brevis|minimus)/i,
    /gracilis[_\s]muscle/i,
    /pectineus[_\s]muscle/i,
  ],
  erectors: [
    /(iliocostalis|longissimus|spinalis)[_\s](thoracis|lumborum)/i,
    /multifidus[_\s](thoracis|lumborum)/i,
    /quadratus[_\s]lumborum/i,
  ],
} as const satisfies Readonly<Record<MuscleId, ReadonlyArray<RegExp>>>;

const GENERIC_MUSCLE_PATTERN = /\bmuscle\b|muscul/i;

const HIDE_PATTERNS = [
  /\bbone\b/i,
  /skeleton/i,
  /vertebra/i,
  /\brib\b/i,
  /\bskull\b/i,
  /\bcrani/i,
  /\bjoint\b/i,
  /cartilage/i,
  /\bdisc\b/i,
  /\bdisk\b/i,
  /ligament/i,
  /tendon/i,
  /aponeurosis/i,
  /fascia/i,
  /retinaculum/i,
  /artery/i,
  /\bvein\b/i,
  /vena\b/i,
  /vessel/i,
  /capillary/i,
  /nerve/i,
  /ganglion/i,
  /plexus/i,
  /lymph/i,
  /\bnode\b/i,
  /thymus/i,
  /spleen/i,
  /heart/i,
  /atrium/i,
  /ventricle/i,
  /aorta/i,
  /lung/i,
  /bronch/i,
  /trache/i,
  /pharynx/i,
  /larynx/i,
  /epiglott/i,
  /esophag/i,
  /stomach/i,
  /intestin/i,
  /liver/i,
  /pancreas/i,
  /gallbladder/i,
  /kidney/i,
  /bladder/i,
  /ureter/i,
  /urethra/i,
  /uterus/i,
  /ovary/i,
  /testis/i,
  /prostate/i,
  /penis/i,
  /vagina/i,
  /scrotum/i,
  /\beye\b/i,
  /eyeball/i,
  /\bear\b/i,
  /cochlea/i,
  /malleus/i,
  /incus/i,
  /stapes/i,
  /tongue/i,
  /\btooth\b/i,
  /teeth/i,
  /palate/i,
  /gland/i,
  /salivary/i,
  /thyroid/i,
  /parathyroid/i,
  /metacarpal/i,
  /metatarsal/i,
  /phalanx/i,
  /phalanges/i,
  /\bcarpal/i,
  /\btarsal/i,
  /femur/i,
  /tibia/i,
  /fibula/i,
  /humerus/i,
  /radius/i,
  /ulna/i,
  /scapula/i,
  /clavicle/i,
  /pelvis/i,
  /sacrum/i,
  /\bilium\b/i,
  /ischium/i,
  /pubis/i,
  /patella/i,
  /sternum/i,
  /hyoid/i,
  /brain/i,
  /cerebrum/i,
  /cerebell/i,
  /\bspinal\b/i,
  /medulla/i,
  /\bpons\b/i,
] as const satisfies ReadonlyArray<RegExp>;

export class MuscleClassifier {
  private static readonly CLASSIFY_CACHE = new Map<string, MuscleClassification>();

  static buildNameChain(object: THREE.Object3D): Array<string> {
    const names: Array<string> = [];
    let current: THREE.Object3D | null = object;
    while (isNotNil(current)) {
      if (current.name !== "") names.push(current.name);

      current = current.parent;
    }

    return names;
  }

  static classifyMesh(object: THREE.Object3D): MuscleClassification {
    const joinedName = MuscleClassifier.buildNameChain(object).join(" | ");

    const cached = MuscleClassifier.CLASSIFY_CACHE.get(joinedName);
    if (isNotNil(cached)) return cached;

    let result: MuscleClassification = { kind: "hidden" };

    if (HIDE_PATTERNS.some((pattern) => pattern.test(joinedName))) {
      result = { kind: "hidden" };
    } else {
      let isMatched = false;
      for (const muscleId of ALL_MUSCLE_IDS) {
        const patterns = MUSCLE_NAME_PATTERNS[muscleId];
        if (patterns.some((pattern) => pattern.test(joinedName))) {
          result = { kind: "muscle", muscleId };
          isMatched = true;
          break;
        }
      }
      if (!isMatched) {
        result = GENERIC_MUSCLE_PATTERN.test(joinedName) ? { kind: "generic" } : { kind: "hidden" };
      }
    }

    MuscleClassifier.CLASSIFY_CACHE.set(joinedName, result);

    return result;
  }
}
